import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import type { FlexProps } from './flex.component';
import { Flex } from './flex.component';

interface SpacerPropsVertical {
	vertical: string;
	horizontal?: never;
	asChild?: never;
	children?: never;
}

interface SpacerPropsHorizontal {
	vertical?: never;
	horizontal: string;
	asChild?: never;
	children?: never;
}

interface SpacerPropsWithChild {
	vertical?: string;
	horizontal?: string;
	/**
	 * Merges its props onto its immediate child.
	 */
	asChild?: boolean;
	children?: ReactNode;
}

export type SpacerProps = {
	basis?: React.CSSProperties['flexBasis'];
} & (SpacerPropsVertical | SpacerPropsHorizontal | SpacerPropsWithChild);

function Spacer({ vertical, horizontal, basis, asChild, children, ...props }: SpacerProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const Comp = asChild ? Slot : Flex;

	const { style: unknownStyle, ...rest } = props as unknown as FlexProps<'div'>;
	const style: CSSProperties =
		children != null
			? {
					marginTop: vertical,
					marginRight: horizontal,
					marginBottom: vertical,
					marginLeft: horizontal,
				}
			: {
					width: horizontal,
					height: vertical,
				};

	return (
		<Comp
			ref={ref}
			style={{
				flexBasis: basis,
				flexShrink: 0,
				flexGrow: 0,
				...style,
				...(unknownStyle ?? {}),
			}}
			{...rest}
		>
			{children}
		</Comp>
	);
}

/**
 * `<Spacer />` is a general purpose container with Cellula semantics that can be
 * used for custom spacing within a stack of items. It supports Cellula tokens
 * to ensure consistency with other Cellula components.
 */
const _Spacer = forwardRef(Spacer);

export { _Spacer as Spacer };

Spacer.displayName = 'CellulaSpacer';
